import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { ActionContent } from '@/src/modules/CampaignsModule/screens/ActionContent';

import ActionsLayout from './ActionsLayout';

import { AUTOMATION_PATHS } from '@/modules/AutomationModuleNew/constants';
import RouterSkeleton from '@/modules/RulesModule/components/RouterSkeleton';
import { RULES_TYPES_ALLOW } from '@/modules/RulesModule/constants';
import { useRuleData } from '@/modules/RulesModule/hooks';
import { useResetRulesState } from '@/modules/RulesModule/hooks/useResetRulesState';
import RulesLayout from '@/modules/RulesModule/layouts/RulesLayout';
import RulesDetail from '@/modules/RulesModule/screens/RulesDetail';
import RulesEmailMain from '@/modules/RulesModule/screens/RulesEmail/RulesEmailMain';

//TODO: Temporal router for mixed content from V1 & V2 experiences
const ActionContentRouter = () => {
  const { pathname } = useLocation();
  const { actionId, actionType } = useParams();
  const showRulesModuleV2 = useFeatureFlag('showRulesModuleV2');
  const showNewAutomationModule = useFeatureFlag('showNewAutomationModule');

  const { rule, getRuleData, getEmailInfo } = useRuleData(Number(actionId));
  const { resetAllAtomStates } = useResetRulesState();
  const isEmailPath = pathname.includes('/email');
  const isAutomationPath = pathname.includes(AUTOMATION_PATHS.MODULE_PATH);

  const allowTrigger = useMemo(
    () => RULES_TYPES_ALLOW.includes(rule?.trigger?.id as string),
    [rule?.trigger?.id]
  );

  const isEmailTrigger = useMemo(() => actionType === 'email-trigger', [actionType]);

  const showNewAutomationContent = useMemo(
    () => isEmailTrigger && showNewAutomationModule && allowTrigger && isAutomationPath,
    [isEmailTrigger, showNewAutomationModule, allowTrigger, isAutomationPath]
  );

  const showRulesContent = useMemo(
    () => (isEmailTrigger && showRulesModuleV2 && allowTrigger) || showNewAutomationContent,
    [isEmailTrigger, showRulesModuleV2, allowTrigger, showNewAutomationContent]
  );

  const lastParam = useMemo(() => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    return pathSegments[pathSegments.length - 1];
  }, []);

  useEffect(() => {
    void getRuleData();
    void getEmailInfo();
  }, [getEmailInfo, getRuleData]);

  useEffect(() => {
    return () => resetAllAtomStates();
  }, [resetAllAtomStates]);

  if (rule?.trigger?.value === 'Flows' && !pathname.endsWith('/email') && showRulesModuleV2) {
    return <Navigate to={`${pathname}/email`} />;
  }

  if (Object.keys(rule).length === 0 && lastParam !== 'email' && isEmailTrigger)
    return <RouterSkeleton />;
  return (
    <Routes>
      <Route element={showRulesContent ? <RulesLayout /> : <ActionsLayout />}>
        <Route index element={showRulesContent ? <RulesDetail /> : <ActionContent />} />
        {(showRulesContent || isEmailPath) && <Route element={<RulesEmailMain />} path="email" />}
      </Route>
    </Routes>
  );
};

export default ActionContentRouter;
